import React from "react";
import { Stack, Paper, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SendIcon from "@mui/icons-material/Send";
import type { ChildFeeAccount, ChildFeePayment, ParentChild } from "../../../types/parentFamily";
import { formatShortDate } from "../../../utils/parentFamilyHelpers";
import {
  dayTypeLabel,
  formatLedgerCurrency,
  paymentPlanLabel,
  summarizeSchoolFees,
} from "../../../utils/schoolFeesHelpers";
import {
  accountSubtitle,
  formatShortDate as formatFeeAccountShortDate,
  summarizeFeeAccount,
} from "../../../utils/feeAccountHelpers";
import { feeSendButtonLabel, getChildGuardianUserIds } from "../../../utils/feeStatementSend";
import { NoticeboardButton } from "../../shared";
import { CollapsiblePayments } from "./CollapsiblePayments";
import { FeeStatementActionBar } from "./FeeStatementActionBar";
import {
  FAMILY_FEE_ACCOUNT_SX,
  FAMILY_LEDGER_STATEMENT_SX,
} from "./familyStyles";
import type { FamilyDeleteTarget } from "./useFamilyModals";

interface FeeStatementsSectionProps {
  child: ParentChild;
  userId: string;
  sendingFeeId: string | null;
  sendingFeeAccountId: string | null;
  expandedPaymentSections: Set<string>;
  onTogglePaymentSection: (sectionKey: string) => void;
  onOpenFeeSetup: (childId: string) => void;
  onOpenAddFeeAccount: (childId: string) => void;
  onViewLedger: (childId: string) => void;
  onSendFeeStatement: (child: ParentChild) => void;
  onOpenRecordPayment: (childId: string, payment?: ChildFeePayment) => void;
  onSetFeesPaidInFull: (childId: string, paid: boolean) => void;
  onEditFeeSetup: (childId: string) => void;
  onDeleteSchoolFees: (target: FamilyDeleteTarget) => void;
  onDeletePayment: (target: FamilyDeleteTarget) => void;
  onViewFeeAccount: (childId: string, accountId: string) => void;
  onSendFeeAccountStatement: (child: ParentChild, account: ChildFeeAccount) => void;
  onOpenFeeAccountPayment: (
    childId: string,
    accountId: string,
    payment?: ChildFeePayment
  ) => void;
  onEditFeeAccount: (childId: string, account: ChildFeeAccount) => void;
  onCancelFeeAccount: (childId: string, accountId: string) => void;
  onSetFeeAccountPaidInFull: (childId: string, accountId: string, paid: boolean) => void;
  onDeleteFeeAccount: (target: FamilyDeleteTarget) => void;
  onDeleteFeeAccountPayment: (target: FamilyDeleteTarget) => void;
}

export const FeeStatementsSection: React.FC<FeeStatementsSectionProps> = ({
  child,
  userId,
  sendingFeeId,
  sendingFeeAccountId,
  expandedPaymentSections,
  onTogglePaymentSection,
  onOpenFeeSetup,
  onOpenAddFeeAccount,
  onViewLedger,
  onSendFeeStatement,
  onOpenRecordPayment,
  onSetFeesPaidInFull,
  onEditFeeSetup,
  onDeleteSchoolFees,
  onDeletePayment,
  onViewFeeAccount,
  onSendFeeAccountStatement,
  onOpenFeeAccountPayment,
  onEditFeeAccount,
  onCancelFeeAccount,
  onSetFeeAccountPaidInFull,
  onDeleteFeeAccount,
  onDeleteFeeAccountPayment,
}) => {
  const account = child.schoolFees;
  const summary = summarizeSchoolFees(account);
  const feeAccounts = child.feeAccounts ?? [];
  const guardianRecipientCount = getChildGuardianUserIds(child, userId).length;
  const sendLabel = feeSendButtonLabel(guardianRecipientCount);

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="subtitle2">Statements</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {!account ? (
            <NoticeboardButton
              variant="secondary"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => onOpenFeeSetup(child.id)}
            >
              Set up school fees
            </NoticeboardButton>
          ) : null}
          <NoticeboardButton
            variant="secondary"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onOpenAddFeeAccount(child.id)}
          >
            Add charge
          </NoticeboardButton>
        </Stack>
      </Stack>

      <Stack spacing={1.5}>
        {!account ? (
          <Typography variant="body2" color="text.secondary">
            Set up school fees to add the annual statement, or add other charges below.
          </Typography>
        ) : (
          <Paper variant="outlined" sx={FAMILY_LEDGER_STATEMENT_SX}>
            <Stack spacing={0.75}>
              <Typography
                variant="caption"
                sx={{ color: "primary.main", fontWeight: 600, letterSpacing: 0.2 }}
              >
                School fees
              </Typography>
              <Typography variant="body2">
                <strong>{account.academicYear}</strong> ·{" "}
                {formatLedgerCurrency(account.totalAnnualFee, account.currency)} ·{" "}
                {paymentPlanLabel(account.paymentPlanMonths)}
                {account.dayType ? ` · ${dayTypeLabel(account.dayType)}` : ""}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Paid: {formatLedgerCurrency(summary.totalPaid, account.currency)} · Outstanding:{" "}
                {summary.isSettled
                  ? "Paid up"
                  : formatLedgerCurrency(summary.outstanding, account.currency)}
              </Typography>
              {account.lastStatementSentAt ? (
                <Typography variant="caption" color="text.secondary">
                  Last sent: {formatShortDate(account.lastStatementSentAt)}
                </Typography>
              ) : null}
            </Stack>
            <FeeStatementActionBar
              primaryActions={[
                {
                  key: "view",
                  label: "View statement",
                  icon: <VisibilityIcon />,
                  onClick: () => onViewLedger(child.id),
                },
                {
                  key: "send",
                  label: sendingFeeId === child.id ? "Sending…" : sendLabel,
                  icon: <SendIcon />,
                  disabled: sendingFeeId === child.id,
                  onClick: () => onSendFeeStatement(child),
                },
                {
                  key: "record",
                  label: "Record payment",
                  icon: <AddIcon />,
                  disabled: summary.isSettled,
                  onClick: () => onOpenRecordPayment(child.id),
                },
              ]}
              menuActions={[
                {
                  key: "paid-status",
                  label: summary.isSettled ? "Reopen fees" : "Mark paid up",
                  onClick: () =>
                    onSetFeesPaidInFull(child.id, !summary.isSettled),
                },
                {
                  key: "edit",
                  label: "Edit setup",
                  icon: <EditIcon fontSize="small" />,
                  onClick: () => onEditFeeSetup(child.id),
                },
                {
                  key: "delete",
                  label: "Delete school fees",
                  icon: <DeleteIcon fontSize="small" />,
                  destructive: true,
                  onClick: () =>
                    onDeleteSchoolFees({
                      type: "school-fees",
                      childId: child.id,
                      id: child.id,
                      label: `${account.academicYear} school fees`,
                    }),
                },
              ]}
            />
            <CollapsiblePayments
              sectionKey={`school-fees-${child.id}`}
              payments={account.payments}
              currency={account.currency}
              expanded={expandedPaymentSections.has(`school-fees-${child.id}`)}
              onToggle={onTogglePaymentSection}
              onEdit={(payment) => onOpenRecordPayment(child.id, payment)}
              onDelete={(payment) =>
                onDeletePayment({
                  type: "payment",
                  childId: child.id,
                  id: payment.id,
                  label: `Payment ${payment.paymentNumber}`,
                })
              }
            />
          </Paper>
        )}

        {feeAccounts.length === 0 && account ? (
          <Typography variant="body2" color="text.secondary">
            Add once-off payments or monthly extracurricular billing here. These stay separate
            from school fees.
          </Typography>
        ) : null}

        {feeAccounts.map((feeAccount) => {
          const feeSummary = summarizeFeeAccount(feeAccount);
          return (
            <Paper key={feeAccount.id} variant="outlined" sx={FAMILY_FEE_ACCOUNT_SX}>
              <Stack spacing={0.75}>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 600, letterSpacing: 0.2 }}
                >
                  Other charge
                </Typography>
                <Typography variant="body2">
                  <strong>{feeAccount.title}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {accountSubtitle(feeAccount)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Paid: {formatLedgerCurrency(feeSummary.totalPaid, feeAccount.currency)} ·
                  Outstanding:{" "}
                  {feeSummary.isSettled
                    ? "Paid up"
                    : formatLedgerCurrency(feeSummary.outstanding, feeAccount.currency)}
                </Typography>
                {feeAccount.lastStatementSentAt ? (
                  <Typography variant="caption" color="text.secondary">
                    Last sent: {formatFeeAccountShortDate(feeAccount.lastStatementSentAt)}
                  </Typography>
                ) : null}
              </Stack>
              <FeeStatementActionBar
                primaryActions={[
                  {
                    key: "view",
                    label: "View statement",
                    icon: <VisibilityIcon />,
                    onClick: () => onViewFeeAccount(child.id, feeAccount.id),
                  },
                  {
                    key: "send",
                    label:
                      sendingFeeAccountId === feeAccount.id ? "Sending…" : sendLabel,
                    icon: <SendIcon />,
                    disabled: sendingFeeAccountId === feeAccount.id,
                    onClick: () => onSendFeeAccountStatement(child, feeAccount),
                  },
                  {
                    key: "record",
                    label: "Record payment",
                    icon: <AddIcon />,
                    disabled: feeSummary.isSettled,
                    onClick: () => onOpenFeeAccountPayment(child.id, feeAccount.id),
                  },
                ]}
                menuActions={[
                  {
                    key: "edit",
                    label: "Edit charge",
                    icon: <EditIcon fontSize="small" />,
                    onClick: () => onEditFeeAccount(child.id, feeAccount),
                  },
                  ...(feeSummary.isActive && !feeAccount.cancelledAt
                    ? [
                        {
                          key: "cancel",
                          label: "Cancel charge",
                          onClick: () => onCancelFeeAccount(child.id, feeAccount.id),
                        },
                      ]
                    : []),
                  {
                    key: "paid-status",
                    label: feeSummary.isSettled ? "Reopen charge" : "Mark paid up",
                    onClick: () =>
                      onSetFeeAccountPaidInFull(
                        child.id,
                        feeAccount.id,
                        !feeSummary.isSettled
                      ),
                  },
                  {
                    key: "delete",
                    label: "Delete charge",
                    icon: <DeleteIcon fontSize="small" />,
                    destructive: true,
                    onClick: () =>
                      onDeleteFeeAccount({
                        type: "fee-account",
                        childId: child.id,
                        id: feeAccount.id,
                        label: feeAccount.title,
                      }),
                  },
                ]}
              />
              <CollapsiblePayments
                sectionKey={`fee-account-${feeAccount.id}`}
                payments={feeAccount.payments}
                currency={feeAccount.currency}
                expanded={expandedPaymentSections.has(`fee-account-${feeAccount.id}`)}
                onToggle={onTogglePaymentSection}
                onEdit={(payment) =>
                  onOpenFeeAccountPayment(child.id, feeAccount.id, payment)
                }
                onDelete={(payment) =>
                  onDeleteFeeAccountPayment({
                    type: "fee-account-payment",
                    childId: child.id,
                    accountId: feeAccount.id,
                    id: payment.id,
                    label: `Payment ${payment.paymentNumber}`,
                  })
                }
              />
            </Paper>
          );
        })}
      </Stack>
    </Stack>
  );
};
